import type { Point, ScreenWarpMode } from '../types';
import { createCanvas } from './loadImage';

type Quad = [Point, Point, Point, Point];

type WarpOptions = {
  gridX?: number;
  gridY?: number;
  fitMode?: ScreenWarpMode;
  seamFixPx?: number;
  sourceMaxSize?: number;
};

type Triangle = [Point, Point, Point];

type SourceLike = HTMLImageElement | HTMLCanvasElement | HTMLVideoElement | VideoFrame;

type ProjectiveMapper = (u: number, v: number) => Point;

export function warpImageToQuad(
  ctx: CanvasRenderingContext2D,
  image: SourceLike,
  quadInput: Point[],
  options: WarpOptions = {}
): void {
  if (quadInput.length !== 4) {
    throw new Error('screenQuad must contain 4 points in TL, TR, BR, BL order');
  }

  const quad = quadInput as Quad;
  const gridX = Math.max(2, options.gridX ?? 30);
  const gridY = Math.max(2, options.gridY ?? 30);
  const fitMode = normalizeFitMode(options.fitMode);
  const seamFixPx = Math.max(0, options.seamFixPx ?? 2.0);

  const quadAspect = getQuadAspect(quad);
  const source = prepareSource(image, quadAspect, fitMode, options.sourceMaxSize);
  if (tryDrawExactProjectiveWarp(ctx, source, quad)) {
    return;
  }

  const projectPoint = createProjectiveMapper(quad);
  const previousSmoothing = ctx.imageSmoothingEnabled;
  const previousQuality = ctx.imageSmoothingQuality;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = getPreferredWarpSmoothingQuality();

  try {
    for (let y = 0; y < gridY; y += 1) {
      const v0 = y / gridY;
      const v1 = (y + 1) / gridY;

      for (let x = 0; x < gridX; x += 1) {
        const u0 = x / gridX;
        const u1 = (x + 1) / gridX;

        const d00 = projectPoint(u0, v0);
        const d10 = projectPoint(u1, v0);
        const d11 = projectPoint(u1, v1);
        const d01 = projectPoint(u0, v1);

        const s00 = { x: u0 * source.width, y: v0 * source.height };
        const s10 = { x: u1 * source.width, y: v0 * source.height };
        const s11 = { x: u1 * source.width, y: v1 * source.height };
        const s01 = { x: u0 * source.width, y: v1 * source.height };

        drawTexturedTriangle(ctx, source, [s00, s10, s11], [d00, d10, d11], seamFixPx);
        drawTexturedTriangle(ctx, source, [s00, s11, s01], [d00, d11, d01], seamFixPx);
      }
    }
  } finally {
    ctx.imageSmoothingEnabled = previousSmoothing;
    ctx.imageSmoothingQuality = previousQuality;
  }
}

function createProjectiveMapper(quad: Quad): ProjectiveMapper {
  const [topLeft, topRight, bottomRight, bottomLeft] = quad;
  const dx1 = topRight.x - bottomRight.x;
  const dy1 = topRight.y - bottomRight.y;
  const dx2 = bottomLeft.x - bottomRight.x;
  const dy2 = bottomLeft.y - bottomRight.y;
  const sx = topLeft.x - topRight.x + bottomRight.x - bottomLeft.x;
  const sy = topLeft.y - topRight.y + bottomRight.y - bottomLeft.y;
  const denominator = dx1 * dy2 - dx2 * dy1;

  if (Math.abs(denominator) < 1e-6) {
    return (u, v) => bilinearPoint(quad, u, v);
  }

  const g = (sx * dy2 - dx2 * sy) / denominator;
  const h = (dx1 * sy - sx * dy1) / denominator;
  const a = topRight.x - topLeft.x + g * topRight.x;
  const b = bottomLeft.x - topLeft.x + h * bottomLeft.x;
  const c = topLeft.x;
  const d = topRight.y - topLeft.y + g * topRight.y;
  const e = bottomLeft.y - topLeft.y + h * bottomLeft.y;
  const f = topLeft.y;

  return (u, v) => {
    const w = g * u + h * v + 1;
    if (Math.abs(w) < 1e-6) {
      return bilinearPoint(quad, u, v);
    }

    return {
      x: (a * u + b * v + c) / w,
      y: (d * u + e * v + f) / w
    };
  };
}

type WebGLWarpState = {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  positionBuffer: WebGLBuffer;
  texture: WebGLTexture;
  attribPosition: number;
  uniformSize: WebGLUniformLocation;
  uniformH0: WebGLUniformLocation;
  uniformH1: WebGLUniformLocation;
  uniformH2: WebGLUniformLocation;
  uniformSource: WebGLUniformLocation;
};

let _webglWarpState: WebGLWarpState | null = null;

function tryDrawExactProjectiveWarp(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  quad: Quad
): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const width = Math.max(1, ctx.canvas.width);
  const height = Math.max(1, ctx.canvas.height);
  const homography = computeDestinationToSourceHomography(quad, width, height);
  if (!homography) {
    return false;
  }

  const state = getWebGLWarpState(width, height);
  if (!state) {
    return false;
  }

  const { gl } = state;
  if (state.canvas.width !== width || state.canvas.height !== height) {
    state.canvas.width = width;
    state.canvas.height = height;
  }

  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(state.program);

  gl.bindBuffer(gl.ARRAY_BUFFER, state.positionBuffer);
  gl.enableVertexAttribArray(state.attribPosition);
  gl.vertexAttribPointer(state.attribPosition, 2, gl.FLOAT, false, 0, 0);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, state.texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

  gl.uniform1i(state.uniformSource, 0);
  gl.uniform2f(state.uniformSize, width, height);
  gl.uniform3f(state.uniformH0, homography[0], homography[1], homography[2]);
  gl.uniform3f(state.uniformH1, homography[3], homography[4], homography[5]);
  gl.uniform3f(state.uniformH2, homography[6], homography[7], 1);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  if (gl.getError() !== gl.NO_ERROR) {
    return false;
  }

  ctx.drawImage(state.canvas, 0, 0);
  return true;
}

function getWebGLWarpState(width: number, height: number): WebGLWarpState | null {
  if (_webglWarpState) {
    return _webglWarpState;
  }

  const canvas = createCanvas(width, height);
  const gl = canvas.getContext('webgl', {
    alpha: true,
    antialias: true,
    premultipliedAlpha: true,
    preserveDrawingBuffer: true
  });
  if (!gl) {
    return null;
  }

  const vertexShader = compileShader(
    gl,
    gl.VERTEX_SHADER,
    `
      attribute vec2 a_position;
      uniform vec2 u_size;
      varying vec2 v_pixel;

      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_pixel = vec2(
          (a_position.x * 0.5 + 0.5) * u_size.x,
          (0.5 - a_position.y * 0.5) * u_size.y
        );
      }
    `
  );
  const fragmentShader = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    `
      precision highp float;

      uniform sampler2D u_source;
      uniform vec2 u_size;
      uniform vec3 u_h0;
      uniform vec3 u_h1;
      uniform vec3 u_h2;
      varying vec2 v_pixel;

      void main() {
        vec3 p = vec3(v_pixel / u_size, 1.0);
        float denominator = dot(u_h2, p);
        if (abs(denominator) < 0.0000001) {
          discard;
        }

        vec2 uv = vec2(dot(u_h0, p), dot(u_h1, p)) / denominator;
        // Tolerance 0.05 — 100x wider than the original 0.0005. At extreme
        // perspective (e.g. when top quad edge collapses near a vanishing
        // line), the float32 perspective divide pushes UV outside [0,1] by
        // 0.01-0.04 for pixels that ARE inside the quad. We keep those, only
        // discarding pixels that are clearly outside (UV beyond 0.05 over the
        // edge), to avoid edge-clamp smearing.
        if (uv.x < -0.05 || uv.x > 1.05 || uv.y < -0.05 || uv.y > 1.05) {
          discard;
        }
        vec2 clampedUv = clamp(uv, 0.0, 1.0);
        gl_FragColor = texture2D(u_source, vec2(clampedUv.x, 1.0 - clampedUv.y));
      }
    `
  );
  if (!vertexShader || !fragmentShader) {
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    return null;
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    return null;
  }

  const positionBuffer = gl.createBuffer();
  const texture = gl.createTexture();
  const uniformSize = gl.getUniformLocation(program, 'u_size');
  const uniformH0 = gl.getUniformLocation(program, 'u_h0');
  const uniformH1 = gl.getUniformLocation(program, 'u_h1');
  const uniformH2 = gl.getUniformLocation(program, 'u_h2');
  const uniformSource = gl.getUniformLocation(program, 'u_source');
  if (!positionBuffer || !texture || !uniformSize || !uniformH0 || !uniformH1 || !uniformH2 || !uniformSource) {
    return null;
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      -1, 1,
      1, -1,
      1, 1
    ]),
    gl.STATIC_DRAW
  );

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  _webglWarpState = {
    canvas,
    gl,
    program,
    positionBuffer,
    texture,
    attribPosition: gl.getAttribLocation(program, 'a_position'),
    uniformSize,
    uniformH0,
    uniformH1,
    uniformH2,
    uniformSource
  };
  return _webglWarpState;
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) {
    return null;
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

function computeDestinationToSourceHomography(quad: Quad, width: number, height: number): number[] | null {
  const source = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 }
  ];
  const rows: number[][] = [];

  for (let index = 0; index < 4; index += 1) {
    const destinationX = quad[index].x / Math.max(1, width);
    const destinationY = quad[index].y / Math.max(1, height);
    const sourceX = source[index].x;
    const sourceY = source[index].y;

    rows.push([
      destinationX,
      destinationY,
      1,
      0,
      0,
      0,
      -sourceX * destinationX,
      -sourceX * destinationY,
      sourceX
    ]);
    rows.push([
      0,
      0,
      0,
      destinationX,
      destinationY,
      1,
      -sourceY * destinationX,
      -sourceY * destinationY,
      sourceY
    ]);
  }

  const solution = solveLinearSystem(rows);
  return solution ? [...solution, 1] : null;
}

function solveLinearSystem(matrix: number[][]): number[] | null {
  const size = 8;

  for (let column = 0; column < size; column += 1) {
    let pivotRow = column;
    let pivotValue = Math.abs(matrix[column][column]);
    for (let row = column + 1; row < size; row += 1) {
      const value = Math.abs(matrix[row][column]);
      if (value > pivotValue) {
        pivotValue = value;
        pivotRow = row;
      }
    }

    if (pivotValue < 1e-12) {
      return null;
    }

    if (pivotRow !== column) {
      const temp = matrix[column];
      matrix[column] = matrix[pivotRow];
      matrix[pivotRow] = temp;
    }

    const pivot = matrix[column][column];
    for (let item = column; item <= size; item += 1) {
      matrix[column][item] /= pivot;
    }

    for (let row = 0; row < size; row += 1) {
      if (row === column) {
        continue;
      }
      const factor = matrix[row][column];
      for (let item = column; item <= size; item += 1) {
        matrix[row][item] -= factor * matrix[column][item];
      }
    }
  }

  return matrix.map((row) => row[size]);
}

function prepareSource(
  image: SourceLike,
  targetAspect: number,
  fitMode: 'fit' | 'crop',
  sourceMaxSize?: number
): HTMLCanvasElement {
  const { width: imageWidth, height: imageHeight } = getSourceSize(image);
  const imageAspect = imageWidth / Math.max(1, imageHeight);
  const naturalBase = Math.max(imageWidth, imageHeight, 1024);
  const cappedBase = sourceMaxSize ? Math.max(1024, Math.round(sourceMaxSize)) : naturalBase;
  const base = Math.max(1024, Math.min(naturalBase, cappedBase));

  let width: number;
  let height: number;
  if (targetAspect >= 1) {
    width = base;
    height = Math.max(1, Math.round(base / targetAspect));
  } else {
    height = base;
    width = Math.max(1, Math.round(base * targetAspect));
  }

  const sourceCanvas = createCanvas(width, height);
  const sourceCtx = sourceCanvas.getContext('2d');
  if (!sourceCtx) {
    throw new Error('2D context is not available for source canvas');
  }

  sourceCtx.clearRect(0, 0, width, height);

  if (fitMode === 'fit') {
    // Fit: stretch the full image to the quad corners.
    sourceCtx.drawImage(image, 0, 0, imageWidth, imageHeight, 0, 0, width, height);
    return sourceCanvas;
  }

  // Crop: preserve aspect ratio by cropping the source to targetAspect before warp.
  let sx = 0;
  let sy = 0;
  let sw = imageWidth;
  let sh = imageHeight;

  if (imageAspect > targetAspect) {
    // Source is wider than target: crop left/right, keep top+bottom.
    sw = imageHeight * targetAspect;
    sx = (imageWidth - sw) / 2;
  } else if (imageAspect < targetAspect) {
    // Source is taller/narrower than target: crop top/bottom.
    sh = imageWidth / targetAspect;
    sy = (imageHeight - sh) / 2;
  }

  sourceCtx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);

  return sourceCanvas;
}

function getPreferredWarpSmoothingQuality(): ImageSmoothingQuality {
  return isWindowsChromiumWarpRuntime() ? 'medium' : 'high';
}

function isWindowsChromiumWarpRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isWindows = /Win/i.test(platform) || /Windows/i.test(userAgent);
  const isChromiumShell = /Chrome|Chromium|CriOS|Edg|OPR/i.test(userAgent);
  const isFirefoxShell = /Firefox|FxiOS/i.test(userAgent);
  return isWindows && isChromiumShell && !isFirefoxShell;
}

function getSourceSize(source: SourceLike): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return {
      width: Math.max(1, source.videoWidth || source.width || 1),
      height: Math.max(1, source.videoHeight || source.height || 1)
    };
  }

  if (source instanceof HTMLImageElement) {
    return {
      width: Math.max(1, source.naturalWidth || source.width || 1),
      height: Math.max(1, source.naturalHeight || source.height || 1)
    };
  }

  // VideoFrame uses displayWidth / displayHeight
  if (typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) {
    return {
      width: Math.max(1, source.displayWidth || 1),
      height: Math.max(1, source.displayHeight || 1)
    };
  }

  return {
    width: Math.max(1, (source as HTMLCanvasElement).width || 1),
    height: Math.max(1, (source as HTMLCanvasElement).height || 1)
  };
}

function drawTexturedTriangle(
  ctx: CanvasRenderingContext2D,
  source: SourceLike,
  sourceTriangle: Triangle,
  destinationTriangle: Triangle,
  seamFixPx: number
): void {
  const matrix = computeAffineMatrix(sourceTriangle, destinationTriangle);
  if (!matrix) {
    return;
  }
  const clipTriangle = seamFixPx > 0 ? expandTriangle(destinationTriangle, seamFixPx) : destinationTriangle;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(clipTriangle[0].x, clipTriangle[0].y);
  ctx.lineTo(clipTriangle[1].x, clipTriangle[1].y);
  ctx.lineTo(clipTriangle[2].x, clipTriangle[2].y);
  ctx.closePath();
  ctx.clip();

  ctx.transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f);
  ctx.drawImage(source, 0, 0);
  ctx.restore();
}

function computeAffineMatrix(source: Triangle, destination: Triangle): {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
} | null {
  const [s0, s1, s2] = source;
  const [d0, d1, d2] = destination;

  const denominator =
    s0.x * (s1.y - s2.y) +
    s1.x * (s2.y - s0.y) +
    s2.x * (s0.y - s1.y);

  if (Math.abs(denominator) < 1e-6) {
    return null;
  }

  const a =
    (d0.x * (s1.y - s2.y) + d1.x * (s2.y - s0.y) + d2.x * (s0.y - s1.y)) /
    denominator;
  const b =
    (d0.y * (s1.y - s2.y) + d1.y * (s2.y - s0.y) + d2.y * (s0.y - s1.y)) /
    denominator;
  const c =
    (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) /
    denominator;
  const d =
    (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) /
    denominator;
  const e =
    (d0.x * (s1.x * s2.y - s2.x * s1.y) +
      d1.x * (s2.x * s0.y - s0.x * s2.y) +
      d2.x * (s0.x * s1.y - s1.x * s0.y)) /
    denominator;
  const f =
    (d0.y * (s1.x * s2.y - s2.x * s1.y) +
      d1.y * (s2.x * s0.y - s0.x * s2.y) +
      d2.y * (s0.x * s1.y - s1.x * s0.y)) /
    denominator;

  return { a, b, c, d, e, f };
}

function expandTriangle(triangle: Triangle, amount: number): Triangle {
  const centroid = {
    x: (triangle[0].x + triangle[1].x + triangle[2].x) / 3,
    y: (triangle[0].y + triangle[1].y + triangle[2].y) / 3
  };

  return triangle.map((point) => {
    const dx = point.x - centroid.x;
    const dy = point.y - centroid.y;
    const length = Math.hypot(dx, dy) || 1;
    return {
      x: point.x + (dx / length) * amount,
      y: point.y + (dy / length) * amount
    };
  }) as Triangle;
}

function bilinearPoint(quad: Quad, u: number, v: number): Point {
  const top = lerpPoint(quad[0], quad[1], u);
  const bottom = lerpPoint(quad[3], quad[2], u);
  return lerpPoint(top, bottom, v);
}

function lerpPoint(a: Point, b: Point, t: number): Point {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t
  };
}

function normalizeFitMode(input: ScreenWarpMode | undefined): 'fit' | 'crop' {
  if (input === 'fit' || input === 'contain') {
    return 'fit';
  }
  return 'crop';
}

function getQuadAspect(quad: Quad): number {
  const top = edgeLength(quad[0], quad[1]);
  const bottom = edgeLength(quad[3], quad[2]);
  const left = edgeLength(quad[0], quad[3]);
  const right = edgeLength(quad[1], quad[2]);

  const width = (top + bottom) / 2;
  const height = (left + right) / 2;
  const aspect = width / Math.max(1e-6, height);

  if (!Number.isFinite(aspect) || aspect <= 0) {
    return 1;
  }
  return Math.max(0.05, Math.min(20, aspect));
}

function edgeLength(a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}
